import { useEffect, useMemo, useState } from "react";
import axios from "axios";
import { toast } from "sonner";
import {
  AlertTriangle,
  CheckCircle2,
  File,
  FileText,
  Image as ImageIcon,
  Loader2,
  Sparkles,
} from "lucide-react";
import { Badge } from "../ui/badge";
import { Button } from "../ui/button";
import { Checkbox } from "../ui/checkbox";
import {
  Dialog,
  DialogContent,
  DialogDescription,
  DialogFooter,
  DialogHeader,
  DialogTitle,
} from "../ui/dialog";

const API_URL = process.env.REACT_APP_BACKEND_URL;

/** Sezioni che possono contenere documenti clinici rilevanti */
const SEZIONI_CLINICHE = [
  "sanitaria",
  "piani_terapeutici",
  "richiesta_rsa",
  "terapie_ausili",
  "generale",
  "legali",
  "anagrafica",
  "amministrativa",
  "sociale",
  "contratto_firmato",
];

const LABEL_SEZIONE = {
  sanitaria: "Sanitaria",
  piani_terapeutici: "Piani terapeutici",
  richiesta_rsa: "Richiesta RSA",
  terapie_ausili: "Terapie e Ausili",
  generale: "Generale",
  legali: "Documenti legali",
  anagrafica: "Anagrafica",
  amministrativa: "Amministrativa",
  sociale: "Sociale",
  contratto_firmato: "Contratto firmato",
};

/** Sezioni pre-selezionate di default perché più spesso contengono diagnosi */
const SEZIONI_PRESELEZIONATE = new Set(["sanitaria", "piani_terapeutici", "richiesta_rsa"]);

const MAX_DOCUMENTI = 5;

function isImageDoc(doc) {
  if (doc.file_type?.startsWith("image/")) return true;
  const ext = (doc.file_ext || "").toLowerCase();
  return [".jpg", ".jpeg", ".png", ".gif", ".webp"].includes(ext);
}

function isPdfDoc(doc) {
  if (doc.file_type === "application/pdf") return true;
  return (doc.file_ext || "").toLowerCase() === ".pdf";
}

function DocIcon({ doc }) {
  if (isImageDoc(doc)) return <ImageIcon className="h-3.5 w-3.5 text-blue-500 shrink-0" />;
  if (isPdfDoc(doc)) return <FileText className="h-3.5 w-3.5 text-red-500 shrink-0" />;
  return <File className="h-3.5 w-3.5 text-slate-400 shrink-0" />;
}

const FIELD_LABELS = {
  diagnosi_principale: "Diagnosi principale",
  diagnosi_secondarie: "Diagnosi secondarie",
  patologie_croniche: "Patologie croniche",
  allergie: "Allergie",
  note_sanitarie: "Note sanitarie",
};

function FieldPreview({ fields }) {
  const items = Object.entries(FIELD_LABELS)
    .map(([key, label]) => {
      const v = fields[key];
      if (!v) return null;
      if (Array.isArray(v) && v.length === 0) return null;
      const display = Array.isArray(v) ? v.join(", ") : v;
      if (!String(display).trim()) return null;
      return { key, label, display };
    })
    .filter(Boolean);

  if (items.length === 0) {
    return <p className="text-sm text-slate-500">Nessun dato rilevante estratto.</p>;
  }

  return (
    <dl className="space-y-2 text-sm">
      {items.map(({ key, label, display }) => (
        <div key={key}>
          <dt className="text-xs text-slate-500 mb-0.5">{label}</dt>
          <dd className="font-medium text-slate-800 break-words leading-snug">{display}</dd>
        </div>
      ))}
    </dl>
  );
}

export default function EstrazionePatologieDialog({
  open,
  onOpenChange,
  fascicoloId,
  token,
  documenti = [],
  onApply,
}) {
  const [selectedIds, setSelectedIds] = useState([]);
  const [analyzing, setAnalyzing] = useState(false);
  const [result, setResult] = useState(null);

  /** Documenti raggruppati per sezione, solo sezioni con almeno un file */
  const groupedDocs = useMemo(() => {
    const groups = {};
    (documenti || []).forEach((doc) => {
      const sezione = doc.sezione || "generale";
      if (!groups[sezione]) groups[sezione] = [];
      groups[sezione].push(doc);
    });
    return SEZIONI_CLINICHE.filter((s) => groups[s]?.length).map((sezione) => ({
      sezione,
      label: LABEL_SEZIONE[sezione] || sezione,
      docs: groups[sezione],
    }));
  }, [documenti]);

  /** Inizializza selezione predefinita quando il dialog si apre */
  useEffect(() => {
    if (!open) return;
    setResult(null);
    const defaults = (documenti || [])
      .filter((doc) => SEZIONI_PRESELEZIONATE.has(doc.sezione || ""))
      .map((doc) => doc.file_id);
    setSelectedIds(defaults.length > 0 ? defaults : []);
  }, [open, documenti]);

  const toggleDoc = (id) => {
    setSelectedIds((prev) =>
      prev.includes(id) ? prev.filter((x) => x !== id) : [...prev, id]
    );
  };

  const handleAnalyze = async () => {
    if (selectedIds.length === 0) {
      toast.error("Seleziona almeno un documento da analizzare");
      return;
    }
    if (selectedIds.length > MAX_DOCUMENTI) {
      toast.error(`Seleziona al massimo ${MAX_DOCUMENTI} documenti per analisi`);
      return;
    }

    setAnalyzing(true);
    setResult(null);
    try {
      const { data } = await axios.post(
        `${API_URL}/api/fascicoli-assistenziali/${fascicoloId}/sanitaria/extract-patologie`,
        { file_ids: selectedIds },
        { headers: { Authorization: `Bearer ${token}` } }
      );
      setResult(data);
      if (data.warnings?.length) {
        toast.message("Analisi completata con avvisi", {
          description: data.warnings.join(" · "),
        });
      } else {
        toast.success("Dati sanitari estratti dai documenti");
      }
    } catch (err) {
      toast.error(
        err.response?.data?.detail || "Errore durante l'analisi dei documenti"
      );
    } finally {
      setAnalyzing(false);
    }
  };

  const handleApply = () => {
    if (!result?.fields) return;
    onApply?.({ fields: result.fields });
    onOpenChange(false);
  };

  const handleClose = (value) => {
    if (!value) {
      setResult(null);
      setAnalyzing(false);
    }
    onOpenChange(value);
  };

  const tooManySelected = selectedIds.length > MAX_DOCUMENTI;

  return (
    <Dialog open={open} onOpenChange={handleClose}>
      <DialogContent className="max-w-2xl max-h-[90vh] flex flex-col">
        <DialogHeader>
          <DialogTitle className="flex items-center gap-2">
            <Sparkles className="h-5 w-5 text-violet-600" />
            Estrai patologie con AI
          </DialogTitle>
          <DialogDescription>
            Seleziona i documenti clinici da analizzare (max {MAX_DOCUMENTI}). L&apos;AI legge le prime 6 pagine
            di ogni PDF, usa diagnosi/terapie/allergie già in cartella come contesto e propone i campi per la
            revisione prima di applicarli al tab Sanitario.
          </DialogDescription>
        </DialogHeader>

        {documenti.length === 0 ? (
          <div className="flex flex-col items-center gap-3 py-10 text-center">
            <FileText className="h-10 w-10 text-slate-300" />
            <p className="text-sm text-slate-500">
              Nessun documento allegato al fascicolo.
              <br />
              Carica prima i documenti clinici nel tab Documenti.
            </p>
          </div>
        ) : (
          <>
            <div className="flex items-center justify-between gap-2 border-b pb-3">
              <p className="text-sm text-slate-600">
                {selectedIds.length} di {documenti.length} selezionati
                {tooManySelected && (
                  <span className="ml-2 text-amber-600 font-medium">
                    — max {MAX_DOCUMENTI}
                  </span>
                )}
              </p>
              <div className="flex gap-2">
                <Button
                  type="button"
                  variant="outline"
                  size="sm"
                  onClick={() =>
                    setSelectedIds(documenti.map((d) => d.file_id).slice(0, MAX_DOCUMENTI))
                  }
                >
                  Seleziona tutti (max {MAX_DOCUMENTI})
                </Button>
                <Button
                  type="button"
                  variant="outline"
                  size="sm"
                  onClick={() => setSelectedIds([])}
                >
                  Nessuno
                </Button>
              </div>
            </div>

            <div className="flex-1 overflow-y-auto space-y-4 py-2 pr-1">
              {groupedDocs.map(({ sezione, label, docs }) => (
                <div key={sezione}>
                  <p className="mb-1.5 flex items-center gap-2 text-xs font-semibold uppercase tracking-wide text-slate-500">
                    <Badge variant="outline" className="text-xs">
                      {label}
                    </Badge>
                    {SEZIONI_PRESELEZIONATE.has(sezione) && (
                      <span className="text-violet-600">· consigliata</span>
                    )}
                  </p>
                  <div className="space-y-1.5 pl-1">
                    {docs.map((doc) => {
                      const id = doc.file_id;
                      const checked = selectedIds.includes(id);
                      const disabled = !checked && selectedIds.length >= MAX_DOCUMENTI;
                      return (
                        <label
                          key={id}
                          htmlFor={`pat-doc-${id}`}
                          className={`flex cursor-pointer items-start gap-3 rounded-lg border p-2.5 transition-colors ${
                            checked
                              ? "border-violet-300 bg-violet-50"
                              : disabled
                              ? "cursor-not-allowed border-slate-100 bg-slate-50 opacity-50"
                              : "border-slate-200 bg-white hover:bg-slate-50"
                          }`}
                        >
                          <Checkbox
                            id={`pat-doc-${id}`}
                            checked={checked}
                            disabled={disabled}
                            onCheckedChange={() => !disabled && toggleDoc(id)}
                            className="mt-0.5"
                          />
                          <div className="min-w-0 flex-1">
                            <div className="flex items-center gap-1.5">
                              <DocIcon doc={doc} />
                              <p className="truncate text-sm font-medium text-slate-800">
                                {doc.file_name}
                              </p>
                            </div>
                            {doc.descrizione && (
                              <p className="mt-0.5 truncate text-xs text-slate-400">
                                {doc.descrizione}
                              </p>
                            )}
                          </div>
                        </label>
                      );
                    })}
                  </div>
                </div>
              ))}
            </div>

            {result?.fields && (
              <div className="rounded-xl border border-violet-200 bg-violet-50/60 p-4 space-y-3">
                <div className="flex items-center gap-2 text-sm font-medium text-violet-900">
                  <CheckCircle2 className="h-4 w-4" />
                  Dati riconosciuti — verifica prima di applicare
                </div>
                <FieldPreview fields={result.fields} />
                {result.warnings?.length > 0 && (
                  <div className="flex items-start gap-2 rounded-lg border border-amber-200 bg-amber-50 px-3 py-2">
                    <AlertTriangle className="h-3.5 w-3.5 text-amber-600 mt-0.5 shrink-0" />
                    <p className="text-xs text-amber-800">{result.warnings.join(" · ")}</p>
                  </div>
                )}
              </div>
            )}
          </>
        )}

        <DialogFooter className="border-t pt-4">
          <Button
            type="button"
            variant="outline"
            onClick={() => handleClose(false)}
            disabled={analyzing}
          >
            Annulla
          </Button>
          {!result?.fields ? (
            <Button
              type="button"
              onClick={handleAnalyze}
              disabled={analyzing || selectedIds.length === 0 || tooManySelected}
              className="bg-violet-600 hover:bg-violet-700"
            >
              {analyzing ? (
                <>
                  <Loader2 className="mr-2 h-4 w-4 animate-spin" />
                  Analisi in corso…
                </>
              ) : (
                <>
                  <Sparkles className="mr-2 h-4 w-4" />
                  Analizza con AI ({selectedIds.length})
                </>
              )}
            </Button>
          ) : (
            <Button
              type="button"
              onClick={handleApply}
              className="bg-violet-600 hover:bg-violet-700"
            >
              <CheckCircle2 className="mr-2 h-4 w-4" />
              Applica al fascicolo
            </Button>
          )}
        </DialogFooter>
      </DialogContent>
    </Dialog>
  );
}
